import { Link, useParams } from "react-router-dom";
import styles from "../Blog/Blog.module.scss";
import { useLanguage } from "../contexts/LanguageContext";
import { getBlogCategoryLabel } from "../utils/blogCategoryLabel";
import {
  categories,
  tagLinks,
  categoryArticles,
  isVisibleBlogArticle,
  isVisibleBlogTag,
} from "../Blog/Blog";
import {
  getLocalizedValue,
  localizeArticleList,
  localizeCategoryItems,
  localizeTagItems,
} from "../utils/localizedContent";
import { blogText } from "../i18n/blog";

export default function BlogArchive() {
  const { categoryKey, tagKey } = useParams();
  const { lang } = useLanguage();
  const t = blogText[lang] || blogText.JP;
  const localizedCategories = localizeCategoryItems(categories, lang);
  const localizedTagLinks = localizeTagItems(
    tagLinks.filter(isVisibleBlogTag),
    lang,
  );
  const localizedCategoryArticles = Object.fromEntries(
    Object.entries(categoryArticles).map(([key, articleList]) => [
      key,
      localizeArticleList(articleList.filter(isVisibleBlogArticle), lang),
    ]),
  );

  const isTagPage = Boolean(tagKey);
  const activeTag = tagKey ? decodeURIComponent(tagKey) : "";

  const activeCategory = localizedCategories.find(
    (category) => category.key === categoryKey,
  );

  const allArticles = Object.values(localizedCategoryArticles).flat();

  const uniqueArticles = Array.from(
    new Map(allArticles.map((article) => [article.slug, article])).values(),
  );

  const articles = isTagPage
    ? uniqueArticles.filter((article) => {
        const tags = Array.isArray(article.tags) ? article.tags : [];

        return tags.some(
          (tag) => tag.replace("#", "") === activeTag.replace("#", ""),
        );
      })
    : localizedCategoryArticles[categoryKey] || [];

  const pageLabel = isTagPage ? t.archiveTagLabel : t.archiveCategoryLabel;

  const pageTitle = isTagPage
    ? `#${activeTag}${t.articleSuffix}`
    : `${activeCategory?.icon || "📚"} ${
        getLocalizedValue(activeCategory?.label, lang) || "ブログ"
      }${t.articleSuffix}`;

  const pageDescription = isTagPage
    ? `#${activeTag} ${t.archiveTagDescription}`
    : `${
        getLocalizedValue(activeCategory?.label, lang) || "宮古島"
      }${t.archiveCategoryDescription}`;

  return (
    <div className={styles.root}>
      <section className={styles.archiveHero}>
        <Link to="/blog">{t.backToBlog}</Link>

        <p className={styles.sectionLabel}>{pageLabel}</p>

        <h1>{pageTitle}</h1>

        <p>{pageDescription}</p>
      </section>

      <section className={styles.section}>
        <div className={styles.archiveHeader}>
          <div>
            <p className={styles.sectionLabel}>ARTICLES</p>
            <h2>{t.articleList}</h2>
          </div>

          <span>
            {articles.length} {t.articlesCount}
          </span>
        </div>

        {articles.length > 0 ? (
          <div className={styles.categoryArticleGrid}>
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} lang={lang} />
            ))}
          </div>
        ) : (
          <div className={styles.archiveEmpty}>
            <h3>{t.archiveEmptyTitle}</h3>
            <p>{t.archiveEmptyText}</p>
            <Link to="/blog">{t.backToBlog}</Link>
          </div>
        )}
      </section>

      <section className={`${styles.section} ${styles.archiveNavSection}`}>
        <div className={styles.archiveLinkBox}>
          <div>
            <p className={styles.sectionLabel}>{t.categoryLabel}</p>
            <h2>{t.categoryTitle}</h2>
          </div>

          <div className={styles.categoryGrid}>
            {localizedCategories.map((category) => (
              <Link
                key={category.key}
                to={category.path}
                className={`${styles.categoryCard} ${
                  !isTagPage && category.key === categoryKey
                    ? styles.categoryCardActive
                    : ""
                }`}
              >
                <span>{category.icon}</span>
                <strong>{category.label}</strong>
              </Link>
            ))}
          </div>
        </div>

        <div className={styles.archiveLinkBox}>
          <div>
            <p className={styles.sectionLabel}>{t.tagLabel}</p>
            <h2>{t.tagTitle}</h2>
          </div>

          <div className={styles.archiveTagGrid}>
            {localizedTagLinks.map((tag) => (
              <Link
                key={tag.key}
                to={tag.path}
                className={`${styles.archiveTag} ${
                  isTagPage && activeTag === tag.key
                    ? styles.archiveTagActive
                    : ""
                }`}
              >
                <span>{tag.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function ArticleCard({ article, lang }) {
  const ARTICLE_BASE_PATH = "/blog";
  const articleTags = Array.isArray(article.tags) ? article.tags : [];

  return (
    <Link
      to={`${ARTICLE_BASE_PATH}/${article.slug}`}
      className={styles.articleCard}
    >
      <div className={styles.articleImage}>
        <img src={article.image} alt={article.title} />
        <span>{getBlogCategoryLabel(article.category, lang)}</span>
      </div>

      <div className={styles.articleBody}>
        <h3>{article.title}</h3>
        <p>{article.text}</p>

        {articleTags.length > 0 && (
          <div className={styles.articleTags}>
            {articleTags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}

        <div className={styles.articleMeta}>
          <span>{article.date}</span>
          <span>◷ {article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}
